import React from 'react';
import PropTypes from 'prop-types';
import MetricsExplanation from './MetricsExplanation';
import { USER_ROLES } from '../../utils/auth';

// KPI calculation weights
const QUALITY_WEIGHT = 0.60;  // Quality score weight (60%)
const TIME_WEIGHT = 0.25;     // Time estimation accuracy weight (25%)
const PRODUCTIVITY_WEIGHT = 0.15;  // Productivity score weight (15%)

// Salary adjustment caps for KPI calculation
const SALARY_ADJUSTMENT_MIN = 0.7;  // Minimum factor (max strictness: -30% penalty for high salaries)
const SALARY_ADJUSTMENT_MAX = 1.3;  // Maximum factor (max leniency: +30% bonus for low salaries)

// Reopened-issues penalty: no effect below the threshold; at/above it the KPI
// is multiplied down, scaling from MIN penalty (at the threshold) to MAX
// penalty (when every task is reopened).
const REOPENED_THRESHOLD = 0.30;    // 30% of the developer's tasks in the period
const REOPENED_MIN_PENALTY = 0.10;  // -10% KPI at exactly the threshold
const REOPENED_MAX_PENALTY = 0.30;  // -30% KPI when 100% of tasks are reopened

const UserMetricsPanel = ({
	tasks,
	selectedUserNames,
	multiMode,
	separateView,
	loadingTotalHours = false,
	employeeMetrics = {},
	loadingEmployeeMetrics = false,
	userRole = null,
}) => {
	// Helper function to get color class based on A/E ratio
	const getASRatioColor = (ratio) => {
		if (ratio <= 1.5) return 'text-green-600 font-semibold'; // Excellent
		if (ratio <= 2.0) return 'text-blue-600 font-semibold'; // Good
		if (ratio <= 2.5) return 'text-yellow-600 font-semibold'; // Average
		if (ratio <= 3.0) return 'text-orange-600 font-semibold'; // Bad
		return 'text-red-600 font-semibold'; // Very bad
	};

	// Helper function to get color class based on A/S ratio
	const getAERatioColor = (ratio) => {
		if (ratio <= 0.8) return 'text-green-600 font-semibold'; // Very good
		if (ratio <= 1.0) return 'text-blue-600 font-semibold'; // Good
		if (ratio <= 1.2) return 'text-yellow-600 font-semibold'; // Average
		if (ratio <= 1.5) return 'text-orange-600 font-semibold'; // Bad
		return 'text-red-600 font-semibold'; // Very bad
	};

	// Helper function to get color class based on Efficiency Score
	const getEfficiencyColor = (score) => {
		if (score >= 80) return 'text-green-600 font-bold'; // Excellent
		if (score >= 70) return 'text-blue-600 font-semibold'; // Good
		if (score >= 60) return 'text-yellow-600 font-semibold'; // Average
		if (score >= 50) return 'text-orange-600 font-semibold'; // Below average
		return 'text-red-600 font-semibold'; // Poor
	};

	// Check if a user belongs to designers department
	const isDesigner = (userName) => {
		const data = employeeMetrics[userName];
		return data && data.department === 'designers';
	};

	// Check if admin or admin_designer (salary data visible)
	const canSeeSalary = userRole === USER_ROLES.ADMIN;

	// Calculate metrics by user with proper task-by-task analysis
	const getUserMetrics = () => {
		const userMetrics = {};

		// Initialize metrics for each selected user
		selectedUserNames.forEach(name => {
			userMetrics[name] = {
				userName: name,
				// Separate arrays for tasks with different data
				tasksWithEstimates: [],
				tasksWithStoryPoints: [],
				tasksWithQAPoints: [],
				tasksWithDesignPoints: [],
				allTasks: [],
				// Summary data
				totalActualHours: 0, // Total hours including extended period (-2 months)
				totalActualHoursPeriod: 0, // Hours only for selected period
				totalEstimatedHours: 0,
				totalStoryPoints: 0,
				totalQAPoints: 0,
				totalDesignPoints: 0,
				taskCount: 0,
				completedTaskCount: 0,
				reopenedTaskCount: 0
			};
		});

		// Process each task individually
		tasks.forEach(task => {
			if (task.userName && userMetrics.hasOwnProperty(task.userName)) {
				const metrics = userMetrics[task.userName];
				const actualHours = task.actualHours || 0;
				// Only use totalActualHours if it's explicitly set (not undefined)
				const totalActualHours = task.totalActualHours !== undefined ? task.totalActualHours : actualHours;
				const estimatedHours = task.estimatedHours || task.estimated_hours || 0;
				const storyPoints = task.storyPoints || task.story_points || 0;
				const qaPoints = task.qaPoints || task.qa_points || 0;
				const designPoints = task.designPoints || task.design_points || 0;

				// Add to all tasks
				metrics.allTasks.push({
					gid: task.gid,
					name: task.name,
					actualHours,
					totalActualHours,
					estimatedHours,
					storyPoints,
					qaPoints,
					designPoints
				});

				// Use actualHours for period-based display
				metrics.totalActualHoursPeriod += actualHours;

				// Use totalActualHours for metrics calculation (includes extended period)
				metrics.totalActualHours += totalActualHours;
				metrics.taskCount++;

				// Count completed tasks
				if (task.completed) {
					metrics.completedTaskCount++;
				}

				// Count reopened tasks (Reopened Issues custom field)
				if (task.reopened) {
					metrics.reopenedTaskCount++;
				}

				// Only include in estimates calculation if task has estimated hours
				if (estimatedHours > 0) {
					metrics.tasksWithEstimates.push({
						actualHours: totalActualHours, // Use total hours for A/E ratio
						estimatedHours
					});
					metrics.totalEstimatedHours += estimatedHours;
				}

				// Only include in story points calculation if task has story points
				if (storyPoints > 0) {
					metrics.tasksWithStoryPoints.push({
						actualHours: totalActualHours, // Use total hours for A/S ratio
						storyPoints
					});
					metrics.totalStoryPoints += storyPoints;
				}

				// Only include in QA points calculation if task has QA points
				if (qaPoints > 0) {
					metrics.tasksWithQAPoints.push({
						actualHours: totalActualHours,
						qaPoints
					});
					metrics.totalQAPoints += qaPoints;
				}

				// Only include in Design points calculation if task has design points
				if (designPoints > 0) {
					metrics.tasksWithDesignPoints.push({
						actualHours: totalActualHours,
						designPoints
					});
					metrics.totalDesignPoints += designPoints;
				}
			}
		});

		// Calculate final metrics for each user
		const metricsArray = Object.values(userMetrics)
			.filter(metrics => metrics.taskCount > 0)
			.map(metrics => {
				const userIsDesigner = isDesigner(metrics.userName);

				// NEW LOGIC: A/E and A/S based on total sums
				const aeRatio = metrics.totalEstimatedHours > 0 ? metrics.totalActualHours / metrics.totalEstimatedHours : 0;
				const asRatio = metrics.totalStoryPoints > 0 ? metrics.totalActualHours / metrics.totalStoryPoints : 0;

				// Calculate average QA points (excluding tasks with 0 QA points)
				const avgQaPoints = metrics.tasksWithQAPoints.length > 0 ?
					metrics.totalQAPoints / metrics.tasksWithQAPoints.length : 0;

				// Calculate average Design points (excluding tasks with 0 design points)
				const avgDesignPoints = metrics.tasksWithDesignPoints.length > 0 ?
					metrics.totalDesignPoints / metrics.tasksWithDesignPoints.length : 0;

				// For designers: use design points instead of QA points in KPI
				const qualityPointsAvg = userIsDesigner ? avgDesignPoints : avgQaPoints;

				// Calculate Developer Efficiency Score (KPI)
				// Formula components (each normalized to 0-100 scale):

				// Determine which metrics are available
				const hasQuality = qualityPointsAvg > 0;
				const hasSP = asRatio > 0;
				const hasEstimates = aeRatio > 0;

				// Calculate individual scores

				// 1. Quality Score - based on average QA/Design points
				const qualityScore = qualityPointsAvg > 0 ? qualityPointsAvg : 0;

				// 2. Time Score - based on A/E ratio (ideal = 1.0)
				const timeScore = aeRatio > 0 ? Math.max(0, Math.min(100, (2 - aeRatio) * 100)) : 0;

				// 3. Productivity Score - based on A/S ratio (ideal ≈ 2.0)
				const productivityScore = asRatio > 0 ? Math.max(0, Math.min(100, (2.0 / asRatio) * 100)) : 0;

				// Adaptive weighting based on available metrics
				let efficiencyScore = 0;

				if (hasQuality && hasSP && hasEstimates) {
					efficiencyScore = (
						qualityScore * QUALITY_WEIGHT +
						timeScore * TIME_WEIGHT +
						productivityScore * PRODUCTIVITY_WEIGHT
					);
				} else if (!hasQuality && !hasSP && hasEstimates) {
					efficiencyScore = timeScore;
				} else if (!hasQuality && hasSP && hasEstimates) {
					efficiencyScore = (
						timeScore * 0.50 +
						productivityScore * 0.50
					);
				} else if (hasQuality && !hasSP && hasEstimates) {
					efficiencyScore = (
						qualityScore * 0.50 +
						timeScore * 0.50
					);
				} else if (hasQuality && hasSP && !hasEstimates) {
					efficiencyScore = (
						qualityScore * 0.60 +
						productivityScore * 0.40
					);
				} else {
					efficiencyScore = 0;
				}

				// Reopened-issues penalty — applied to the KPI as a multiplier
				const reopenedRatio = metrics.taskCount > 0
					? metrics.reopenedTaskCount / metrics.taskCount
					: 0;
				const reopenedFactor = reopenedRatio < REOPENED_THRESHOLD
					? 1
					: 1 - Math.min(
						REOPENED_MAX_PENALTY,
						REOPENED_MIN_PENALTY +
							(REOPENED_MAX_PENALTY - REOPENED_MIN_PENALTY) *
							((reopenedRatio - REOPENED_THRESHOLD) / (1 - REOPENED_THRESHOLD))
					);
				efficiencyScore = efficiencyScore * reopenedFactor;

				// OLD LOGIC: Average A/E and A/S ratios per task (kept for reference)
				const avgAeRatio = metrics.tasksWithEstimates.length > 0 ?
					metrics.tasksWithEstimates.reduce((sum, task) => sum + (task.actualHours / task.estimatedHours), 0) / metrics.tasksWithEstimates.length : 0;
				const avgAsRatio = metrics.tasksWithStoryPoints.length > 0 ?
					metrics.tasksWithStoryPoints.reduce((sum, task) => sum + (task.actualHours / task.storyPoints), 0) / metrics.tasksWithStoryPoints.length : 0;

				return {
					userName: metrics.userName,
					isDesigner: userIsDesigner,
					estimatedHours: metrics.totalEstimatedHours,
					actualHours: metrics.totalActualHours,
					actualHoursPeriod: metrics.totalActualHoursPeriod,
					storyPoints: metrics.totalStoryPoints,
					qaPoints: metrics.totalQAPoints,
					avgQaPoints,
					designPoints: metrics.totalDesignPoints,
					avgDesignPoints,
					aeRatio,
					asRatio,
					avgAeRatio,
					avgAsRatio,
					completedTaskCount: metrics.completedTaskCount,
					reopenedTaskCount: metrics.reopenedTaskCount,
					reopenedRatio,
					reopenedFactor,
					efficiencyScore,
					adjustedKPI: 0,
					hasSalaryData: false,
					salaryAdjustmentFactor: 1,
					tasksWithEstimatesCount: metrics.tasksWithEstimates.length,
					tasksWithStoryPointsCount: metrics.tasksWithStoryPoints.length,
					totalTaskCount: metrics.taskCount
				};
			});

		// Apply salary adjustments to KPI with caps
		metricsArray.forEach(metric => {
			const salaryData = employeeMetrics[metric.userName];
			if (salaryData && salaryData.has_salary_data) {
				metric.hasSalaryData = true;

				const cappedAdjustmentFactor = Math.max(
					SALARY_ADJUSTMENT_MIN,
					Math.min(SALARY_ADJUSTMENT_MAX, salaryData.adjustment_factor)
				);

				metric.salaryAdjustmentFactor = cappedAdjustmentFactor;
				metric.adjustedKPI = metric.efficiencyScore * cappedAdjustmentFactor;
			}
		});

		return metricsArray;
	};

	const userMetrics = getUserMetrics();

	// Check if any user has salary data to show Adjusted KPI column (only for admin/admin_designer)
	const showAdjustedKPI = canSeeSalary && userMetrics.some(m => m.hasSalaryData);

	// Don't render if no data
	if (userMetrics.length === 0) {
		return null;
	}

	// Check if any task doesn't have totalActualHours yet
	const hasTotalHours = tasks.length > 0 && tasks.every(task => task.totalActualHours !== undefined);
	const showLoading = loadingTotalHours || !hasTotalHours;

	// Render a single metrics table
	const renderMetricsTable = (metrics, userName = null) => {
		// Determine if we show QA or Design columns for this table's users
		const tableIsDesigners = metrics.every(m => m.isDesigner);
		const tableHasDesigners = metrics.some(m => m.isDesigner);
		const showQACol = !tableIsDesigners; // Hide QA for pure designer tables
		const showDPCol = tableHasDesigners; // Show DP if any designer present

		return (
		<>
			<table className='min-w-full text-sm border-collapse'>
				<thead className='bg-gray-50'>
					<tr>
						{!userName && multiMode && <th className='p-2 text-left'>User</th>}
						<th
							className='p-2 text-right cursor-help hover:text-blue-600 transition-colors'
							data-tooltip-id="tooltip-est"
							data-tooltip-content="Estimated hours for tasks"
						>
							Est. (h)
						</th>
						<th
							className='p-2 text-right cursor-help hover:text-blue-600 transition-colors'
							data-tooltip-id="tooltip-period"
							data-tooltip-content="Actual hours tracked in selected period only"
						>
							P. Act. (h)
						</th>
						<th
							className='p-2 text-right cursor-help font-bold hover:text-blue-600 transition-colors'
							data-tooltip-id="tooltip-total"
							data-tooltip-content="Total actual hours including 3 months before selected period. Used for calculating A/E and A/S ratios."
						>
							Total Act. (h)
						</th>
						<th
							className='p-2 text-right cursor-help hover:text-blue-600 transition-colors'
							data-tooltip-id="tooltip-ae"
							data-tooltip-content="Actual/Estimated ratio (based on Total Act.). Ideal value: ≤1.0. Shows how well you estimate time."
						>
							A/E
						</th>
						<th
							className='p-2 text-right cursor-help hover:text-blue-600 transition-colors'
							data-tooltip-id="tooltip-sp"
							data-tooltip-content="Story Points assigned to tasks"
						>
							SP
						</th>
						<th
							className='p-2 text-right cursor-help hover:text-blue-600 transition-colors'
							data-tooltip-id="tooltip-as"
							data-tooltip-content="Actual/Story Points ratio (based on Total Act.). Ideal value: ≈2.0. Shows hours per story point."
						>
							A/S
						</th>
						{showQACol && (
							<th
								className='p-2 text-right cursor-help hover:text-blue-600 transition-colors'
								data-tooltip-id="tooltip-qa"
								data-tooltip-content="Average QA points across all tasks"
							>
								Avg QA
							</th>
						)}
						{showDPCol && (
							<th
								className='p-2 text-right cursor-help hover:text-blue-600 transition-colors'
								data-tooltip-id="tooltip-dp"
								data-tooltip-content="Average Design Points across all tasks"
							>
								Avg DP
							</th>
						)}
						<th
							className='p-2 text-right cursor-help hover:text-blue-600 transition-colors'
							data-tooltip-id="tooltip-comp"
							data-tooltip-content="Number of completed tasks in selected period"
						>
							Comp.
						</th>
						<th
							className='p-2 text-right cursor-help font-bold hover:text-blue-600 transition-colors'
							data-tooltip-id="tooltip-kpi"
							data-tooltip-content={`Efficiency Score (KPI). Combines Quality (${QUALITY_WEIGHT * 100}%), Time (${TIME_WEIGHT * 100}%), and Productivity (${PRODUCTIVITY_WEIGHT * 100}%) metrics. Score: 80+ Excellent, 70+ Good, 60+ Average, 50+ Below Average, <50 Poor.`}
						>
							KPI
						</th>
						{showAdjustedKPI && (
							<th
								className='p-2 text-right cursor-help font-bold hover:text-blue-600 transition-colors'
								data-tooltip-id="tooltip-adjusted-kpi"
								data-tooltip-content="Salary-adjusted KPI based on department average. Formula: KPI × (2 - salary_coefficient), capped at ±30%. Higher salaries: up to -30% stricter, lower salaries: up to +30% bonus."
							>
								Adj. KPI
							</th>
						)}
					</tr>
				</thead>
				<tbody>
				{metrics.map(m => (
					<tr key={m.userName} className='border-t hover:bg-gray-50'>
						{!userName && multiMode && (
							<td className='p-2 font-medium'>{m.userName}</td>
						)}
						<td className='p-2 text-right'>
							{m.estimatedHours > 0 ? m.estimatedHours.toFixed(2) : ''}
						</td>
						<td className='p-2 text-right'>
							{m.actualHoursPeriod > 0 ? m.actualHoursPeriod.toFixed(2) : ''}
						</td>
						<td className='p-2 text-right font-bold'>
							{showLoading ? (
								<div className='inline-flex items-center justify-end'>
									<div className='w-14 h-4 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-pulse rounded'></div>
								</div>
							) : (
								m.actualHours.toFixed(2)
							)}
						</td>
						<td className={`p-2 text-right ${showLoading ? '' : getAERatioColor(m.aeRatio)}`}>
							{showLoading ? (
								<div className='inline-flex items-center justify-end'>
									<div className='w-12 h-4 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-pulse rounded'></div>
								</div>
							) : (
								m.aeRatio > 0 ? m.aeRatio.toFixed(2) : 'N/A'
							)}
						</td>
						<td className='p-2 text-right'>
							{m.storyPoints > 0 ? m.storyPoints.toFixed(1) : '0'}
						</td>
						<td className={`p-2 text-right ${showLoading ? '' : getASRatioColor(m.asRatio)}`}>
							{showLoading ? (
								<div className='inline-flex items-center justify-end'>
									<div className='w-12 h-4 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-pulse rounded'></div>
								</div>
							) : (
								m.asRatio > 0 ? m.asRatio.toFixed(2) : 'N/A'
							)}
						</td>
						{showQACol && (
							<td className='p-2 text-right'>
								{!m.isDesigner && m.avgQaPoints > 0 ? m.avgQaPoints.toFixed(1) : (m.isDesigner ? '—' : '')}
							</td>
						)}
						{showDPCol && (
							<td className='p-2 text-right'>
								{m.isDesigner && m.avgDesignPoints > 0 ? m.avgDesignPoints.toFixed(1) : (m.isDesigner ? '' : '—')}
							</td>
						)}
						<td className='p-2 text-right font-medium'>
							{m.completedTaskCount}
						</td>
						<td className={`p-2 text-right font-bold ${showLoading ? '' : getEfficiencyColor(m.efficiencyScore)}`}>
							{showLoading ? (
								<div className='inline-flex items-center justify-end'>
									<div className='w-12 h-4 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-pulse rounded'></div>
								</div>
							) : (
								m.efficiencyScore > 0 ? m.efficiencyScore.toFixed(1) : 'N/A'
							)}
						</td>
						{showAdjustedKPI && (
							<td className={`p-2 text-right font-bold ${showLoading || loadingEmployeeMetrics ? '' : (m.hasSalaryData ? getEfficiencyColor(m.adjustedKPI) : 'text-gray-400')}`}>
								{showLoading || loadingEmployeeMetrics ? (
									<div className='inline-flex items-center justify-end'>
										<div className='w-12 h-4 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-pulse rounded'></div>
									</div>
								) : m.hasSalaryData ? (
									m.adjustedKPI > 0 ? m.adjustedKPI.toFixed(1) : 'N/A'
								) : (
									'-'
								)}
							</td>
						)}
					</tr>
				))}
			</tbody>
		</table>

		</>
		);
	};

	return (
		<div className='mt-6'>
			<h3 className='text-lg font-bold mb-2'>Performance Metrics</h3>
			{!separateView || selectedUserNames.length <= 1 ? (
				// Combined view
				<div className='overflow-x-auto'>
					{renderMetricsTable(userMetrics)}
				</div>
			) : (
				// Separate view (grid layout) - each user gets their own table
				<div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
					{userMetrics.map(metrics => (
						<div key={metrics.userName} className='border rounded-lg p-2 bg-white overflow-auto'>
							<h4 className='font-medium mb-2'>{metrics.userName}</h4>
							{renderMetricsTable([metrics], metrics.userName)}
						</div>
					))}
				</div>
			)}

			{/* Metrics Explanation Component */}
			<MetricsExplanation
				showAdjustedKPI={showAdjustedKPI}
				qualityWeight={QUALITY_WEIGHT * 100}
				timeWeight={TIME_WEIGHT * 100}
				productivityWeight={PRODUCTIVITY_WEIGHT * 100}
				hasDesigners={userMetrics.some(m => m.isDesigner)}
				hasNonDesigners={userMetrics.some(m => !m.isDesigner)}
			/>
		</div>
	);
};

UserMetricsPanel.propTypes = {
	tasks: PropTypes.arrayOf(
		PropTypes.shape({
			gid: PropTypes.string.isRequired,
			userName: PropTypes.string,
			actualHours: PropTypes.number.isRequired,
			totalActualHours: PropTypes.number,
			estimatedHours: PropTypes.number,
			estimated_hours: PropTypes.number,
			storyPoints: PropTypes.number,
			story_points: PropTypes.number,
			qaPoints: PropTypes.number,
			qa_points: PropTypes.number,
			designPoints: PropTypes.number,
			design_points: PropTypes.number,
			completed: PropTypes.bool,
		})
	).isRequired,
	selectedUserNames: PropTypes.arrayOf(PropTypes.string).isRequired,
	multiMode: PropTypes.bool,
	separateView: PropTypes.bool,
	loadingTotalHours: PropTypes.bool,
	employeeMetrics: PropTypes.objectOf(
		PropTypes.shape({
			employee: PropTypes.string,
			department: PropTypes.string,
			salary: PropTypes.number,
			department_avg: PropTypes.number,
			coefficient: PropTypes.number,
			adjustment_factor: PropTypes.number,
			has_salary_data: PropTypes.bool,
		})
	),
	loadingEmployeeMetrics: PropTypes.bool,
	userRole: PropTypes.string,
};

export default UserMetricsPanel;
